import { t } from '../i18n';
import { PollQuestion, PollOption } from '../types';
import { post } from '../utils/api';

const POLL_QUESTIONS: PollQuestion[] = [
  {
    step: 1,
    type: 'single',
    title: 'Was ist Ihre Position?',
    options: [
      { value: 'geschaeftsfuehrer', label: 'Geschäftsführer/in', icon: '👔' },
      { value: 'meister', label: 'Meister/in', icon: '🏅' },
      { value: 'projektleiter', label: 'Projektleiter/in', icon: '📋' },
      { value: 'bueroleitung', label: 'Büroleitung', icon: '🏢' },
      { value: 'mitarbeiter', label: 'Mitarbeiter/in', icon: '👷' },
      { value: 'sonstiges', label: 'Sonstiges', icon: '💼' },
    ],
  },
  {
    step: 2,
    type: 'single',
    title: 'Wie viele Mitarbeiter hat Ihr Unternehmen?',
    options: [
      { value: '1-5', label: '1-5', icon: '👤' },
      { value: '6-15', label: '6-15', icon: '👥' },
      { value: '16-50', label: '16-50', icon: '🏗️' },
      { value: '51-100', label: '51-100', icon: '🏭' },
      { value: '100+', label: '100+', icon: '🏛️' },
    ],
  },
  {
    step: 3,
    type: 'multi',
    title: 'Welche Software nutzen Sie aktuell?',
    options: [
      { value: 'excel', label: 'Excel / Tabellen', icon: '📊' },
      { value: 'papier', label: 'Papier', icon: '📝' },
      { value: 'andere-software', label: 'Andere Software', icon: '💻' },
      { value: 'keine', label: 'Keine', icon: '🚫' },
    ],
    allowCustom: true,
  },
  {
    step: 4,
    type: 'multi',
    title: 'Welche Bereiche möchten Sie verbessern?',
    options: [
      { value: 'auftraege', label: 'Auftragsverwaltung', icon: '📋' },
      { value: 'angebote', label: 'Angebote & Rechnungen', icon: '💰' },
      { value: 'zeiterfassung', label: 'Zeiterfassung', icon: '⏰' },
      { value: 'projektplanung', label: 'Projektplanung', icon: '📅' },
      { value: 'kommunikation', label: 'Kommunikation', icon: '💬' },
      { value: 'dokumentation', label: 'Dokumentation', icon: '📁' },
    ],
  },
  {
    step: 5,
    type: 'single',
    title: 'In welchem Gewerk sind Sie tätig?',
    options: [
      { value: 'elektro', label: 'Elektro', icon: '⚡' },
      { value: 'sanitaer', label: 'Sanitär / Heizung', icon: '🔧' },
      { value: 'maler', label: 'Maler / Lackierer', icon: '🎨' },
      { value: 'tischler', label: 'Tischler / Schreiner', icon: '🪵' },
      { value: 'dachdecker', label: 'Dachdecker', icon: '🏠' },
      { value: 'andere', label: 'Anderes Gewerk', icon: '🔨' },
    ],
  },
];

export function renderPollPage(customId: string, onComplete: () => void): HTMLElement {
  const page = document.createElement('div');
  page.className = 'fixed inset-0 z-50 flex items-center justify-center bg-brand-900/95 backdrop-blur-md';

  let currentStep = 0;
  const answers: Record<string, unknown> = {};

  function renderStep() {
    if (currentStep >= POLL_QUESTIONS.length) {
      finishPoll();
      return;
    }

    const question = POLL_QUESTIONS[currentStep];
    const progress = ((currentStep + 1) / POLL_QUESTIONS.length) * 100;
    const isLast = currentStep === POLL_QUESTIONS.length - 1;

    page.innerHTML = `
      <div class="mx-4 w-full max-w-2xl animate-scale-in">
        <div class="rounded-2xl bg-white shadow-elevated overflow-hidden relative">
          <!-- Top accent -->
          <div class="h-1 bg-gradient-to-r from-brand-400 via-brand-500 to-brand-600"></div>

          <div class="p-8 lg:p-10">
            <!-- Progress bar -->
            <div class="mb-8">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center gap-2">
                  <span class="flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">${currentStep + 1}</span>
                  <span class="text-sm font-medium text-surface-500">${t('poll.of')} ${POLL_QUESTIONS.length}</span>
                </div>
                <button id="skip-btn" class="text-sm text-surface-400 hover:text-surface-600 transition-colors">${t('poll.skip')}</button>
              </div>
              <div class="h-1.5 overflow-hidden rounded-full bg-surface-100">
                <div class="h-full rounded-full bg-gradient-to-r from-brand-400 to-brand-500 transition-all duration-700 ease-out" style="width: ${progress}%"></div>
              </div>
            </div>

            <!-- Question -->
            <h3 class="mb-6 font-display text-xl font-bold text-surface-800 lg:text-2xl">${question.title}</h3>

            <!-- Options grid -->
            <div id="options-grid" class="grid gap-3 ${question.options.length <= 4 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}"></div>

            <div id="poll-error" class="mt-4 hidden rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert"></div>

            ${question.allowCustom ? `
              <div class="mt-4">
                <input type="text" id="custom-input" placeholder="Andere Software..."
                  class="oh-input-card">
              </div>
            ` : ''}

            <!-- Action buttons -->
            <div class="mt-8 flex items-center justify-between">
              ${currentStep > 0 ? `
                <button id="prev-btn" class="flex items-center gap-1.5 text-sm font-medium text-surface-500 hover:text-surface-700 transition-colors">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                  Zurück
                </button>
              ` : '<div></div>'}
              <button id="next-btn" class="oh-btn-cta gap-2 text-sm px-6 py-3" disabled>
                ${isLast ? t('poll.finish') : t('poll.next')}
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${isLast ? 'M5 13l4 4L19 7' : 'M13 7l5 5m0 0l-5 5m5-5H6'}"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    const optionsGrid = page.querySelector('#options-grid')!;
    const nextBtn = page.querySelector('#next-btn') as HTMLButtonElement;
    const customInput = page.querySelector('#custom-input') as HTMLInputElement | null;
    const selected = new Set<string>();

    function updateNextButton() {
      nextBtn.disabled = selected.size === 0 && !customInput?.value.trim();
    }

    function renderOptions() {
      optionsGrid.innerHTML = '';
      for (let i = 0; i < question.options.length; i++) {
        const option = question.options[i];
        const btn = document.createElement('button');
        const isSelected = selected.has(option.value);
        btn.className = [
          'group flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200',
          isSelected
            ? 'border-brand-500 bg-brand-50 shadow-sm ring-1 ring-brand-500/20'
            : 'border-surface-200 hover:border-brand-300 hover:bg-surface-50',
        ].join(' ');
        btn.style.animationDelay = `${i * 0.05}s`;
        btn.innerHTML = `
          <span class="text-2xl transition-transform duration-200 ${isSelected ? 'scale-110' : 'group-hover:scale-105'}">${option.icon}</span>
          <span class="text-sm font-medium ${isSelected ? 'text-brand-700' : 'text-surface-700'}">${option.label}</span>
          ${isSelected ? `
            <svg class="ml-auto h-5 w-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          ` : ''}
        `;
        btn.addEventListener('click', () => handleSelect(option));
        optionsGrid.appendChild(btn);
      }
    }

    function handleSelect(option: PollOption) {
      if (question.type === 'single') {
        selected.clear();
        selected.add(option.value);
      } else {
        if (selected.has(option.value)) {
          selected.delete(option.value);
        } else {
          selected.add(option.value);
        }
      }
      updateNextButton();
      renderOptions();
    }

    renderOptions();
    customInput?.addEventListener('input', updateNextButton);

    page.querySelector('#skip-btn')!.addEventListener('click', () => {
      currentStep++;
      renderStep();
    });

    page.querySelector('#prev-btn')?.addEventListener('click', () => {
      if (currentStep > 0) {
        currentStep--;
        renderStep();
      }
    });

    nextBtn.addEventListener('click', async () => {
      const errorEl = page.querySelector('#poll-error') as HTMLElement;
      errorEl.classList.add('hidden');
      nextBtn.disabled = true;
      const originalButton = nextBtn.innerHTML;
      nextBtn.textContent = t('poll.saving');

      const stepKey = `step_${question.step}`;
      const values = Array.from(selected);

      if (customInput?.value.trim()) {
        values.push(customInput.value.trim());
      }

      answers[stepKey] = question.type === 'single' ? values[0] : values;

      const response = await post('/poll', {
        customId,
        step: question.step,
        data: { [stepKey]: answers[stepKey] },
      });

      if (!response.success) {
        errorEl.textContent = response.error || t('poll.saveError');
        errorEl.classList.remove('hidden');
        nextBtn.innerHTML = originalButton;
        nextBtn.disabled = false;
        return;
      }

      currentStep++;
      renderStep();
    });
  }

  async function finishPoll() {
    page.innerHTML = `
      <div class="mx-4 w-full max-w-md animate-scale-in">
        <div class="rounded-2xl bg-white p-10 text-center shadow-elevated">
          <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 ring-4 ring-brand-100">
            <svg class="h-8 w-8 animate-spin text-brand-500" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          </div>
          <h3 class="font-display text-xl font-bold text-surface-800">${t('poll.completing')}</h3>
          <p class="mt-2 text-sm text-surface-500 leading-relaxed">Bitte warten Sie einen Moment.</p>
        </div>
      </div>
    `;

    const response = await post(`/poll/${customId}/complete`, {});

    if (!response.success) {
      page.innerHTML = `
        <div class="mx-4 w-full max-w-md animate-scale-in">
          <div class="rounded-2xl bg-white p-10 text-center shadow-elevated">
            <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 ring-4 ring-amber-100">
              <svg class="h-8 w-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 4h.01M10.3 3.4 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.4a2 2 0 0 0-3.4 0Z"/>
              </svg>
            </div>
            <h3 class="font-display text-xl font-bold text-surface-800">Umfrage nicht gespeichert</h3>
            <p class="mt-2 text-sm text-surface-500 leading-relaxed">${response.error || t('poll.completeError')}</p>
            <p class="mt-2 text-xs text-emerald-700">${t('poll.completeError')}</p>
            <div class="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button id="retry-complete" class="oh-btn-cta">${t('poll.retry')}</button>
              <button id="continue-without-poll" class="text-sm font-medium text-surface-500 hover:text-surface-700">${t('poll.continue')}</button>
            </div>
          </div>
        </div>
      `;
      page.querySelector('#retry-complete')?.addEventListener('click', finishPoll);
      page.querySelector('#continue-without-poll')?.addEventListener('click', onComplete);
      return;
    }

    page.innerHTML = `
      <div class="mx-4 w-full max-w-md animate-bounce-in">
        <div class="rounded-2xl bg-white p-10 text-center shadow-elevated">
          <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-100">
            <svg class="h-8 w-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h3 class="font-display text-xl font-bold text-surface-800">Vielen Dank!</h3>
          <p class="mt-2 text-sm text-surface-500 leading-relaxed">Ihre Angaben wurden gespeichert.</p>
        </div>
      </div>
    `;
    setTimeout(onComplete, 1500);
  }

  renderStep();
  return page;
}
